import React from "react";

/**
 * Scoped error card for a dashboard widget.
 *
 * Visual chrome only. This component is deliberately inert with respect to
 * money — it must NEVER render a monetary value, PII, or a stack trace. Full
 * error context stays server-side per Requirement 14 and the
 * `error-handling-observability` spec. The `errorId` is the trace ID a
 * treasurer can quote to support.
 */
export interface WidgetErrorProps {
  /** Trace ID (UUID v4) that correlates to a CloudWatch log entry. */
  errorId: string;
  /** Optional widget name — surfaced in the card title, e.g. "Arrears unavailable". */
  name?: string;
  /** Invoked when the user clicks the retry button. */
  onRetry?: () => void;
}

export function WidgetError({
  errorId,
  name,
  onRetry,
}: WidgetErrorProps): JSX.Element {
  const title = name ? `${name} unavailable` : "Widget unavailable";
  const bodyName = name ?? "this widget";

  return (
    <div
      role="alert"
      data-testid="widget-error"
      className="w-full h-full flex flex-col justify-center gap-2 px-4 py-3 border rounded-xl"
      style={{
        backgroundColor: "#fef2f2",
        borderColor: "#fecaca",
        borderWidth: 1,
      }}
    >
      <div className="text-sm font-semibold" style={{ color: "#991b1b" }}>
        {title}
      </div>
      <div className="text-xs" style={{ color: "#7f1d1d" }}>
        We couldn't load {bodyName}. ErrorId:{" "}
        <span
          data-testid="widget-error-id"
          className="font-mono"
          style={{ fontFamily: "monospace" }}
        >
          {errorId}
        </span>
      </div>
      {onRetry && (
        <div>
          <button
            type="button"
            onClick={onRetry}
            className="font-medium hover:underline"
            style={{ color: "#2563eb", fontSize: 12 }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
